import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import Project from "../../../components/Project";

import PersonAdd from "@mui/icons-material/PersonAdd";
import styles from "../ProjectContainer.module.scss";

import plusIcon from "../../../assets/editor/plus_icon.svg";

import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import LeftNavigation from "../../../common/views/LeftNavigation";
import { useSelector } from "react-redux";
import TeamTopSection from "./TeamTopSection";

import Teammate from "./Teammate";
import UtilityButton from "../../../components/UtilityButton";
import InviteTeammateModal from "../../../components/InviteTeammateModal";
import DashboardToolbar from "../../../components/DashboardToolbar";
import ProjectService from "../../../service/ProjectService";
import { useProjectFetcher } from "../../../utils/ProjectsFetcherHooks";
import { ROUTES } from "../../../utils/Constant";
import { toast } from "react-toastify";

const TeamProjects = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const { fetchMyProjectsAndUpdateStore } = useProjectFetcher();
  const [modalOpen, setModalOpen] = useState(false);

  //At load, fetch all projects to get the latest information
  useEffect(() => {
    fetchMyProjectsAndUpdateStore();
  }, [fetchMyProjectsAndUpdateStore, teamId]); //id is required to fetch each time we select a new team

  const userInfo = useSelector((state) => state.userInfo);

  const teamsState = useSelector((state) => state.myTeams);
  const teams = useMemo(() => {
    return teamsState?.teams;
  }, [teamsState]);
  const teamsLoaded = useMemo(() => {
    return teamsState?.loaded;
  }, [teamsState]);

  const team = useMemo(() => {
    return teams?.find((t) => t.id === teamId);
  }, [teams, teamId]);

  const projectsState = useSelector((state) => state.projects);

  const projects = useMemo(() => {
    return projectsState?.projects.filter((p) => p.team_id === teamId);
  }, [projectsState, teamId]);

  const projectsLoaded = useMemo(() => {
    return projectsState?.loaded;
  }, [projectsState]);

  const addProject = useCallback(async () => {
    const bodyData = {
      name: `${t("project.project")} (${projects.length + 1})`,
      teamId: teamId,
    };

    const res = await ProjectService.createProject(bodyData);

    return res.data.data.id;
  }, [teamId, projects.length, t]);

  const onClick = useCallback(async () => {
    await addProject();
    await fetchMyProjectsAndUpdateStore();
  }, [addProject, fetchMyProjectsAndUpdateStore]);

  const isCurrentUser = useCallback(
    (teammateUserId) => {
      if (userInfo.id === teammateUserId) {
        return true;
      }
      return false;
    },
    [userInfo.id]
  );

  const canDeleteUser = useCallback(
    (teammateUserId) => {
      //If user is designer, can remove him/her self from the team
      if (!userInfo.isDesigner) {
        if (isCurrentUser(teammateUserId)) {
          return false;
        }
      }

      if (team?.teammates.length <= 1) {
        return false;
      }

      return true;
    },
    [team, isCurrentUser, userInfo.isDesigner]
  );

  useEffect(() => {
    if (teamsLoaded && !team) {
      toast.warn(t("team.not_found"));
      navigate(ROUTES.HOME);
      return;
    }
  }, [navigate, teamsLoaded, team, t]);

  //If admin or designer, we allow to see the email of the teammates
  const canShowEmail = useMemo(() => {
    return userInfo.isDesigner || userInfo.isAdmin ? true : false;
  }, [userInfo]);

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{`${t("pages.team.title", { id: team?.name })}`}</title>
      </Helmet>
      <DashboardToolbar />
      <div className={styles.dashboardContainer}>
        {projectsLoaded && teamsLoaded ? (
          <React.Fragment>
            <LeftNavigation />
            <div className={styles.centerContainer}>
              <TeamTopSection
                canDeleteTeam={userInfo.canDeleteTeam}
                canRenameTeam={userInfo.canRenameTeam}
                team={team}
              />
              <InviteTeammateModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                teamId={teamId}
              />
              {userInfo.canDeleteTeam && (
                <div className={styles.utilityButtonContainer}>
                  <UtilityButton
                    text={t("navigation.create_new_project")}
                    subtext={t("navigation.get_started_project")}
                    icon={plusIcon}
                    onClick={onClick}
                  ></UtilityButton>
                  <UtilityButton
                    text={t("invite.invite_teammate")}
                    subtext={t("invite.invite_button_description")}
                    icon={plusIcon}
                    onClick={() => setModalOpen(true)}
                  ></UtilityButton>
                </div>
              )}
              <div className={styles.addProjectContainer}>
                {projects.map((project) => (
                  <Project
                    key={project.id}
                    projectId={project.id}
                    name={project.name}
                  />
                ))}
              </div>
            </div>
            <div className={styles.rightTeamSection}>
              <div className={styles.rightTopSection}>
                <h5>{t("project.team")}</h5>
                <div
                  className={styles.inviteTeammate}
                  onClick={() => setModalOpen(true)}
                >
                  <PersonAdd fontSize="small" />
                  <span>{t("project.invite_teammate")}</span>
                </div>
              </div>
              {team?.teammates.map((teammate) => (
                <Teammate
                  first_name={teammate.first_name}
                  last_name={teammate.last_name}
                  email={teammate.email}
                  roles={teammate.roles}
                  canDelete={canDeleteUser(teammate.id)}
                  key={teammate.id}
                  teamId={team.id}
                  teammateUserId={teammate.id}
                  isCurrentUser={isCurrentUser(teammate.id)}
                  canShowEmail={canShowEmail}
                />
              ))}
            </div>
          </React.Fragment>
        ) : (
          <div className={styles.loadingProgress}>
            <CircularProgress />
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamProjects;
